import React from 'react';
import { motion } from 'framer-motion';
import { GlassCard } from '../ui/GlassCard';
import { BarChart3, MousePointerClick, Eye, Download, Mail, Clock, Activity, BrainCircuit, Target, Users, Map, Compass } from 'lucide-react';
// === Mini-viz components ===
function FlatBaseline() {
  return <svg viewBox="0 0 100 30" className="w-full h-full" preserveAspectRatio="none" data-id="element-26">
      <line x1="0" y1="22" x2="100" y2="22" stroke="rgba(13,15,20,0.25)" strokeWidth="1" vectorEffect="non-scaling-stroke" data-id="element-27" />
    </svg>;
}
function Sparkline({
  color
}: {
  color: string;
}) {
  const pts = [12, 8, 14, 10, 18, 15, 22, 19, 26, 24, 30];
  const d = pts.map((y, i) => `${i / (pts.length - 1) * 100},${30 - y}`).join(' L ');
  return <svg viewBox="0 0 100 30" className="w-full h-full" preserveAspectRatio="none" data-id="element-28">
      <motion.path d={`M ${d}`} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" initial={{
      pathLength: 0
    }} animate={{
      pathLength: 1
    }} transition={{
      duration: 1.2,
      ease: 'easeOut'
    }} data-id="element-29" />
    </svg>;
}
function PathFlow({
  color
}: {
  color: string;
}) {
  return <svg viewBox="0 0 100 30" className="w-full h-full" preserveAspectRatio="none" data-id="element-30">
      <motion.path d="M 2 22 Q 18 8 32 18 T 62 12 T 96 20" fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" initial={{
      pathLength: 0
    }} animate={{
      pathLength: 1
    }} transition={{
      duration: 1.4,
      ease: 'easeInOut'
    }} data-id="element-31" />
    </svg>;
}
function SegmentBar({
  color
}: {
  color: string;
}) {
  const segs = [40, 28, 18, 14];
  let acc = 0;
  return <svg viewBox="0 0 100 30" className="w-full h-full" preserveAspectRatio="none" data-id="element-32">
      {segs.map((w, i) => {
      const x = acc;
      acc += w;
      return <motion.rect key={i} x={x + 0.5} y={10} width={w - 1} height={10} fill={color} fillOpacity={1 - i * 0.22} rx="1.5" initial={{
        scaleX: 0
      }} animate={{
        scaleX: 1
      }} style={{
        transformOrigin: `${x}px center`
      }} transition={{
        duration: 0.7,
        delay: i * 0.1,
        ease: 'easeOut'
      }} data-id="element-33" />;
    })}
    </svg>;
}
function Heatmap({
  color
}: {
  color: string;
}) {
  const cells = Array.from({
    length: 24
  }).map(() => Math.random());
  return <svg viewBox="0 0 100 30" className="w-full h-full" preserveAspectRatio="none" data-id="element-34">
      {cells.map((v, i) => {
      const col = i % 12;
      const row = Math.floor(i / 12);
      return <motion.rect key={i} x={col * 8 + 1} y={row * 14 + 1} width={6} height={12} rx="1" fill={color} fillOpacity={0.15 + v * 0.7} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: i * 0.02
      }} data-id="element-35" />;
    })}
    </svg>;
}
function ConfidenceShift({
  color
}: {
  color: string;
}) {
  return <svg viewBox="0 0 100 30" className="w-full h-full" preserveAspectRatio="none" data-id="element-36">
      <line x1="0" y1="22" x2="100" y2="22" stroke="rgba(13,15,20,0.1)" strokeWidth="1" vectorEffect="non-scaling-stroke" data-id="element-37" />
      {[{
      x: 12,
      h: 8
    }, {
      x: 30,
      h: 14
    }, {
      x: 50,
      h: 11
    }, {
      x: 70,
      h: 20
    }, {
      x: 88,
      h: 24
    }].map((b, i) => <motion.rect key={i} x={b.x - 3} y={22 - b.h} width="6" height={b.h} rx="1" fill={color} fillOpacity={0.4 + i * 0.12} initial={{
      scaleY: 0
    }} animate={{
      scaleY: 1
    }} style={{
      transformOrigin: 'center 22px'
    }} transition={{
      duration: 0.5,
      delay: i * 0.1
    }} data-id="element-38" />)}
    </svg>;
}
function CuriosityPing({
  color
}: {
  color: string;
}) {
  const dots = [{
    x: 14,
    y: 10
  }, {
    x: 32,
    y: 20
  }, {
    x: 48,
    y: 8
  }, {
    x: 64,
    y: 18
  }, {
    x: 82,
    y: 12
  }, {
    x: 92,
    y: 22
  }];
  return <svg viewBox="0 0 100 30" className="w-full h-full" preserveAspectRatio="none" data-id="element-39">
      {dots.map((d, i) => <g key={i} data-id="element-40">
          <motion.circle cx={d.x} cy={d.y} r="2.5" fill={color} initial={{
        opacity: 0,
        scale: 0
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: i * 0.08
      }} data-id="element-41" />
          <motion.circle cx={d.x} cy={d.y} r="2.5" fill="none" stroke={color} strokeWidth="0.6" vectorEffect="non-scaling-stroke" animate={{
        r: [2.5, 6],
        opacity: [0.7, 0]
      }} transition={{
        duration: 1.8,
        repeat: Infinity,
        delay: i * 0.15
      }} data-id="element-42" />
        </g>)}
    </svg>;
}
// === Data ===
const TRADITIONAL = [{
  label: 'Opens',
  icon: Mail,
  value: '4,201'
}, {
  label: 'Click-through',
  icon: MousePointerClick,
  value: '2.4%'
}, {
  label: 'Impressions',
  icon: Eye,
  value: '12,054'
}, {
  label: 'Downloads',
  icon: Download,
  value: '342'
}, {
  label: 'Time on page',
  icon: Clock,
  value: '1:08'
}, {
  label: 'Bounce rate',
  icon: BarChart3,
  value: '38%'
}];
const BEHAVIORAL = [{
  label: 'Decision pathways',
  icon: Map,
  viz: PathFlow,
  desc: 'How users actually navigate'
}, {
  label: 'Deep-dive exploration',
  icon: Activity,
  viz: Sparkline,
  desc: 'Where attention compounds'
}, {
  label: 'Poll choices',
  icon: Users,
  viz: SegmentBar,
  desc: 'Which options resonate'
}, {
  label: 'Confidence shifts',
  icon: BrainCircuit,
  viz: ConfidenceShift,
  desc: 'How belief evolves'
}, {
  label: 'Segment differences',
  icon: Target,
  viz: Heatmap,
  desc: 'Who behaves differently'
}, {
  label: 'Curiosity patterns',
  icon: Compass,
  viz: CuriosityPing,
  desc: 'What sparks interest'
}];
// === Row renderers ===
type TradRowProps = {
  row: (typeof TRADITIONAL)[number];
  muted?: boolean;
  delay?: number;
};
function TraditionalRow({
  row,
  muted = false,
  delay = 0
}: TradRowProps) {
  return <motion.div className={`grid grid-cols-[auto_1fr_120px_auto] items-center gap-3 px-3 py-2.5 border-b border-snakker-border last:border-b-0 ${muted ? 'opacity-60' : ''}`} initial={{
    opacity: 0,
    x: -8
  }} animate={{
    opacity: muted ? 0.6 : 1,
    x: 0
  }} transition={{
    delay
  }} data-id="element-43">
      <div className="w-8 h-8 rounded-md bg-snakker-ink/[0.04] flex items-center justify-center" data-id="element-44">
        <row.icon className="w-4 h-4 text-snakker-subtle" data-id="element-45" />
      </div>
      <span className="text-sm text-snakker-muted" data-id="element-46">{row.label}</span>
      <div className="h-7 opacity-30 grayscale" data-id="element-47">
        <FlatBaseline data-id="element-48" />
      </div>
      <span className="font-mono text-sm text-snakker-ink/70 w-16 text-right" data-id="element-49">
        {row.value}
      </span>
    </motion.div>;
}
function BehavioralRow({
  row,
  color,
  delay = 0
}: {
  row: (typeof BEHAVIORAL)[number];
  color: string;
  delay?: number;
}) {
  const Viz = row.viz;
  return <motion.div className="grid grid-cols-[auto_1fr_120px_auto] items-center gap-3 px-3 py-2.5 border-b border-snakker-border last:border-b-0" initial={{
    opacity: 0,
    x: 8
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    delay
  }} data-id="element-50">
      <div className="w-8 h-8 rounded-md bg-snakker-primary/10 flex items-center justify-center" data-id="element-51">
        <row.icon className="w-4 h-4" style={{
        color
      }} data-id="element-52" />
      </div>
      <div className="flex flex-col leading-tight" data-id="element-53">
        <span className="text-sm font-medium text-snakker-ink" data-id="element-54">
          {row.label}
        </span>
        <span className="text-[11px] text-snakker-muted" data-id="element-55">{row.desc}</span>
      </div>
      <div className="h-7" data-id="element-56">
        <Viz color={color} data-id="element-57" />
      </div>
      <span className="font-mono text-[11px] text-snakker-primary uppercase tracking-wider w-16 text-right" data-id="element-58">
        Live
      </span>
    </motion.div>;
}
export function Slide02Problem() {
  return <div className="w-full h-full flex flex-col max-w-7xl mx-auto pt-4" data-id="element-59">
      <motion.h2 className="text-[clamp(2rem,3.2vw,3.2rem)] font-light tracking-tight mb-2 text-center text-snakker-ink" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-60">
        Everything you measure today.{' '}
        <span className="text-gradient-accent font-medium" data-id="element-61">
          Plus the behavior behind it.
        </span>
      </motion.h2>
      <motion.p className="text-center text-snakker-muted text-sm mb-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.2
    }} data-id="element-62">
        Snakker delivers the quantitative <em data-id="element-63">and</em> the qualitative — in one
        system.
      </motion.p>

      <div className="flex-1 flex gap-6 items-stretch mb-6 min-h-0" data-id="element-64">
        {/* LEFT: Traditional only */}
        <motion.div className="w-1/2 flex flex-col" initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.2
      }} data-id="element-65">
          <div className="mb-3 flex items-center justify-between" data-id="element-66">
            <div className="flex items-center gap-2" data-id="element-67">
              <div className="w-1.5 h-1.5 rounded-full bg-snakker-subtle" data-id="element-68" />
              <h3 className="text-xs font-mono tracking-widest uppercase text-snakker-muted" data-id="element-69">
                Traditional Reporting
              </h3>
            </div>
            <span className="text-[10px] font-mono text-snakker-subtle uppercase tracking-wider" data-id="element-70">
              Activity only
            </span>
          </div>

          <GlassCard className="flex-1 bg-snakker-ink/[0.015] p-2 flex flex-col justify-center" data-id="element-71">
            {TRADITIONAL.map((row, i) => <TraditionalRow key={i} row={row} delay={0.4 + i * 0.06} data-id="element-72" />)}
          </GlassCard>
        </motion.div>

        {/* RIGHT: Traditional (muted) + Behavioral (live) */}
        <motion.div className="w-1/2 flex flex-col" initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.4
      }} data-id="element-73">
          <div className="mb-3 flex items-center justify-between" data-id="element-74">
            <div className="flex items-center gap-2" data-id="element-75">
              <div className="w-1.5 h-1.5 rounded-full bg-snakker-primary shadow-[0_0_8px_rgba(0,133,189,0.5)]" data-id="element-76" />
              <h3 className="text-xs font-mono tracking-widest uppercase text-snakker-primary" data-id="element-77">
                Snakker — Activity + Behavior
              </h3>
            </div>
            <span className="text-[10px] font-mono text-snakker-primary uppercase tracking-wider" data-id="element-78">
              Both
            </span>
          </div>

          <GlassCard className="flex-1 border-snakker-primary/25 shadow-[0_8px_30px_rgba(0,133,189,0.06)] p-2 flex flex-col" data-id="element-79">
            {/* Block 1: same traditional metrics, muted to signal "we cover this too" */}
            {TRADITIONAL.map((row, i) => <TraditionalRow key={`t-${i}`} row={row} muted delay={0.5 + i * 0.04} data-id="element-80" />)}

            {/* Divider: "+ Behavioral intelligence" */}
            <motion.div className="flex items-center gap-3 px-3 py-2 my-1" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.9
          }} data-id="element-81">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-snakker-primary/40 to-transparent" data-id="element-82" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-snakker-primary whitespace-nowrap" data-id="element-83">
                + Behavioral intelligence
              </span>
              <div className="flex-1 h-px bg-gradient-to-r from-snakker-primary/40 via-snakker-primary/40 to-transparent" data-id="element-84" />
            </motion.div>

            {/* Block 2: unique Snakker behavioral signals, in color */}
            {BEHAVIORAL.map((row, i) => <BehavioralRow key={`b-${i}`} row={row} color={i % 2 === 0 ? '#0085bd' : '#7c5cf5'} delay={1.0 + i * 0.07} data-id="element-85" />)}
          </GlassCard>
        </motion.div>
      </div>

      <motion.div className="text-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.8
    }} data-id="element-86">
        <p className="text-base font-light tracking-wide text-snakker-ink" data-id="element-87">
          Most pharma reporting measures{' '}
          <span className="text-snakker-subtle line-through decoration-snakker-subtle/50" data-id="element-88">
            activity
          </span>
          . Snakker measures{' '}
          <span className="text-snakker-primary font-medium" data-id="element-89">
            activity + behavior
          </span>
          .
        </p>
      </motion.div>
    </div>;
}